import { CommonModule } from '@angular/common';
import { Component, input } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  Fieldset,
  InputAddress,
  FileUploader,
  FormatInputDirective,
} from '@millionshow/client-libs';

@Component({
  selector: 'app-booth-company',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    Fieldset,
    InputAddress,
    FileUploader,
    FormatInputDirective,
  ],
  template: `
    <div [formGroup]="form()" class="flex flex-col gap-6">
      <h2 class="text-lg font-semibold text-zinc-800">업체 정보</h2>

      <!-- 업체 정보 입력 폼 -->
      <div class="flex flex-col gap-6">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <app-fieldset label="회사명" required>
            <input
              type="text"
              formControlName="companyName"
              class="input w-full"
              placeholder="입력해주세요" />
          </app-fieldset>

          <app-fieldset label="대표자명" required>
            <input
              type="text"
              formControlName="ceoName"
              class="input w-full"
              placeholder="입력해주세요" />
          </app-fieldset>

          <app-fieldset label="사업자등록번호" required>
            <input
              type="text"
              formControlName="businessNumber"
              appFormatInput="business"
              placeholder="123-45-67890"
              maxlength="12"
              class="input w-full" />
          </app-fieldset>

          <app-fieldset label="판매 품목" required="required">
            <input
              type="text"
              formControlName="products"
              class="input w-full"
              placeholder="입력해주세요" />
          </app-fieldset>

          <app-fieldset label="대표 전화번호" required="required">
            <input
              type="text"
              formControlName="phone"
              appFormatInput="phone"
              placeholder="02-1234-5678"
              maxlength="13"
              class="input w-full" />
          </app-fieldset>

          <app-fieldset label="대표 팩스번호" required="false">
            <input
              type="text"
              formControlName="fax"
              appFormatInput="phone"
              placeholder="02-1234-5678"
              maxlength="13"
              class="input w-full" />
          </app-fieldset>
        </div>

        <app-fieldset label="주소" required>
          <app-input-address formControlName="address" />
          <input
            type="text"
            formControlName="detailAddress"
            class="input w-full mt-2"
            placeholder="상세 주소를 입력해주세요" />
        </app-fieldset>

        <div class="grid grid-cols-1 gap-4">
          <app-fieldset label="회사 홈페이지" required="false">
            <input
              type="text"
              formControlName="homepage"
              class="input w-full"
              placeholder="주소 내용을 입력해주세요" />
          </app-fieldset>
          <app-fieldset label="브랜드명(간판제작)" required>
            <input
              type="text"
              formControlName="brandName"
              class="input w-full"
              placeholder="주소 내용을 입력해주세요" />
          </app-fieldset>
        </div>

        <app-fieldset label="사업자등록증" required>
          <app-file-uploader
            formControlName="files"
            accept="application/pdf"
            [maxlength]="1"
            [uploadHandler]="uploadHandler()"
            [deleteHandler]="deleteHandler()"
            [isBoothApplication]="true" />
          <p class="text-sm text-gray-500 mt-2">
            * PDF 파일만 업로드 가능합니다.
          </p>
        </app-fieldset>

        <app-fieldset class="relative" label="업체소개" required="false">
          <textarea
            formControlName="description"
            class="input w-full min-h-[100px] resize-none"
            placeholder="입력해주세요"
            style="white-space: normal"></textarea>
          <div class="absolute bottom-4 right-2 text-sm text-zinc-300 z-10">
            {{ form().get('description')?.value?.length || 0 }}/500
          </div>
        </app-fieldset>

        <!-- 전시 담당자 정보 -->
        <div class="manager-section">
          <h3 class="section-title">전시 담당자</h3>
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <app-fieldset label="담당자 이름" required>
              <input
                type="text"
                formControlName="managerName"
                class="input w-full"
                placeholder="OOO / 입력해주세요" />
            </app-fieldset>

            <app-fieldset label="부서/직위" required="false">
              <div class="flex gap-2">
                <input
                  type="text"
                  formControlName="managerDept"
                  class="input w-full"
                  placeholder="입력해주세요" />
                <span class="flex items-center">/</span>
                <input
                  type="text"
                  formControlName="managerTitle"
                  class="input w-full"
                  placeholder="입력해주세요" />
              </div>
            </app-fieldset>

            <app-fieldset label="회사 직통번호" required="false">
              <input
                type="text"
                formControlName="managerPhone"
                appFormatInput="phone"
                placeholder="02-1234-5678"
                maxlength="13"
                class="input w-full" />
            </app-fieldset>

            <app-fieldset label="담당자 연락처" required>
              <input
                type="text"
                formControlName="managerMobile"
                appFormatInput="phone"
                placeholder="010-1234-5678"
                maxlength="13"
                class="input w-full" />
            </app-fieldset>

            <app-fieldset label="담당자 이메일" required>
              <input
                type="email"
                formControlName="managerEmail"
                class="input w-full"
                placeholder="입력해주세요" />
            </app-fieldset>

            <app-fieldset label="담당자 팩스번호" required="false">
              <input
                type="text"
                formControlName="managerFax"
                appFormatInput="phone"
                placeholder="02-1234-5678"
                maxlength="13"
                class="input w-full" />
            </app-fieldset>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      .manager-section {
        margin-top: 2rem;
        padding-top: 2rem;
        border-top: 2px solid #f3f4f6;
      }

      .section-title {
        font-size: 1.125rem;
        font-weight: 600;
        color: #374151;
        margin-bottom: 1.5rem;
      }
    `,
  ],
})
export class BoothCompanyComponent {
  form = input.required<FormGroup>();
  uploadHandler = input.required<any>();
  deleteHandler = input.required<any>();
}
